import { useEffect, useRef, type RefObject } from 'react';
import type { BrowserEnvResponse } from '@lp/shared';
import { api } from '../../lib/api.js';

/**
 * Environment variables for an embedded app, over the postMessage bridge.
 *
 * The app asks with exactly `{ type: 'lp:env-request' }`; this page answers
 * `{ type: 'lp:env', env: { NAME: value, … } }` (an empty `env` when there
 * are none, or they could not be loaded). As with progress events, only a
 * message whose source is this card's own frame is answered, and only the
 * exact payload: anything else is ignored.
 *
 * The answer goes to `'*'` because the frame's origin is opaque ("null") and
 * cannot be named. It is sent to the frame's window only, and only in reply
 * to that window's own request, so it reaches whatever the frame is showing
 * when it asks — the author's app, unless that app navigates itself away.
 *
 * The variables are fetched once per mount, on the first request; an app
 * without any is answered straight away, so `lpEnv()` never hangs.
 */
export const EMBED_ENV_SNIPPET = `function lpEnv() {
  return new Promise((resolve) => {
    addEventListener('message', function onEnv(e) {
      if (e.source !== parent || !e.data || e.data.type !== 'lp:env') return;
      removeEventListener('message', onEnv);
      resolve(e.data.env);
    });
    parent.postMessage({ type: 'lp:env-request' }, '*');
  });
}

const env = await lpEnv();   // e.g. env.OPENAI_API_KEY`;

export function useEmbedEnvBridge(
  frameRef: RefObject<HTMLIFrameElement | null>,
  featureId: string,
  /** Only an uploaded embedded app is answered; an external embed never is. */
  active: boolean,
  hasEnv: boolean,
): void {
  const envRef = useRef<Promise<Record<string, string>> | null>(null);

  useEffect(() => {
    if (!active) return;
    envRef.current = hasEnv ? null : Promise.resolve({});

    const onMessage = (event: MessageEvent<unknown>): void => {
      const frame = frameRef.current?.contentWindow;
      if (!frame || event.source !== frame) return;
      const data = event.data;
      if (typeof data !== 'object' || data === null) return;
      if (Object.keys(data).length !== 1 || (data as { type?: unknown }).type !== 'lp:env-request') {
        return;
      }

      envRef.current ??= api
        .get<BrowserEnvResponse>(`/interactive/${encodeURIComponent(featureId)}/env`)
        .then((r) => r.env)
        .catch(() => {
          envRef.current = null;
          return {};
        });
      void envRef.current.then((env) => frame.postMessage({ type: 'lp:env', env }, '*'));
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [frameRef, featureId, active, hasEnv]);
}
